// Zero-dep data-fetching layer (@tanstack/react-query is 403-blocked by the registry).
// Minimal useQuery/useMutation + a tiny cache with invalidateKeys().
import { useCallback, useEffect, useRef, useState } from 'react';

const listeners = new Set();
// Signal query hooks whose key starts with any invalidated prefix to refetch.
export function invalidateKeys(prefix) {
  listeners.forEach((l) => l(prefix));
}

function keyStr(key) {
  return JSON.stringify(key);
}

// useQuery({ queryKey, queryFn, enabled })
export function useQuery({ queryKey, queryFn, enabled = true }) {
  const [state, setState] = useState({ data: undefined, error: null, isLoading: enabled });
  const fnRef = useRef(queryFn);
  fnRef.current = queryFn;
  const ks = keyStr(queryKey);

  const run = useCallback(() => {
    let alive = true;
    setState((s) => ({ ...s, isLoading: true, error: null }));
    Promise.resolve(fnRef.current())
      .then((data) => alive && setState({ data, error: null, isLoading: false }))
      .catch((error) => alive && setState({ data: undefined, error, isLoading: false }));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setState({ data: undefined, error: null, isLoading: false });
      return;
    }
    const cancel = run();
    return cancel;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ks, enabled]);

  // Refetch when an invalidated prefix matches this key.
  useEffect(() => {
    const l = (prefix) => {
      if (keyStr(queryKey).startsWith(keyStr(prefix).slice(0, -1))) run();
    };
    listeners.add(l);
    return () => listeners.delete(l);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ks, run]);

  return { ...state, refetch: run };
}

// useMutation({ mutationFn, onSuccess, onError })
export function useMutation({ mutationFn, onSuccess, onError }) {
  const [state, setState] = useState({ isPending: false, error: null });
  const mutate = useCallback(
    async (vars) => {
      setState({ isPending: true, error: null });
      try {
        const data = await mutationFn(vars);
        setState({ isPending: false, error: null });
        onSuccess?.(data, vars);
        return data;
      } catch (error) {
        setState({ isPending: false, error });
        onError?.(error, vars);
        throw error;
      }
    },
    [mutationFn, onSuccess, onError]
  );
  return { mutate, ...state };
}
